import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./OfficerDashboard.css";
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

export default function OfficerDashboard() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [officerInfo, setOfficerInfo] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusView, setStatusView] = useState("active"); // "active", "inactive", "all"
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editOfficerInfo, setEditOfficerInfo] = useState({});
  const navigate = useNavigate();
  const officerEmail = (
    localStorage.getItem("officerEmail") || ""
  ).toUpperCase();

  const fetchClients = async () => {
    setLoading(true);
    const snapshot = await getDocs(collection(db, "clients"));
    const clientList = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((c) => c.assignedOfficer === officerEmail);
    setClients(clientList);
    setLoading(false);
  };
  const fetchOfficerInfo = async () => {
    if (!officerEmail) return;
    try {
      const ref = doc(db, "officers", officerEmail);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setOfficerInfo(snap.data());
      }
    } catch (e) {
      console.error("Failed to fetch officer info:", e);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchOfficerInfo();
  }, [officerEmail]);

  // Update lastContactedAt in Firestore when contacting client
  const handleContact = async (clientId) => {
    try {
      await updateDoc(doc(db, "clients", clientId), {
        lastContactedAt: serverTimestamp(),
      });
      // Optionally update local state for immediate UI feedback (not required if you reload from Firestore soon)
      setClients((prev) =>
        prev.map((c) =>
          c.id === clientId
            ? {
                ...c,
                lastContactedAt: { seconds: Math.floor(Date.now() / 1000) },
              }
            : c
        )
      );
    } catch (err) {
      console.error("Failed to update lastContactedAt:", err);
    }
  };

  const setInactive = async (clientId, status) => {
    await updateDoc(doc(db, "clients", clientId), { active: status });
    setClients((prev) =>
      prev.map((c) => (c.id === clientId ? { ...c, active: status } : c))
    );
  };

  const formatDate = (date) => {
    if (!date) return "--";
    try {
      if (date.seconds) {
        // Firestore Timestamp
        const d = new Date(date.seconds * 1000);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      } else if (typeof date === "string" || typeof date === "number") {
        const d = new Date(date);
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      }
      return "--";
    } catch {
      return "--";
    }
  };

  // ---- FILTERING LOGIC ----
  let statusFilteredClients = clients;
  if (statusView === "active") {
    statusFilteredClients = clients.filter((c) => c.active !== false);
  } else if (statusView === "inactive") {
    statusFilteredClients = clients.filter((c) => c.active === false);
  }
  const filteredClients = statusFilteredClients.filter((client) => {
    const fields = [
      client.formDataPurchase?.firstName || "",
      client.formDataPurchase?.lastName || "",
      client.formDataRefinance?.firstName || "",
      client.formDataRefinance?.lastName || "",
      client.formDataPurchase?.email || "",
      client.formDataRefinance?.email || "",
      client.officerApproved === true
        ? "approved"
        : client.officerApproved === false
        ? "denied"
        : "pending",
    ];
    return fields.join(" ").toLowerCase().includes(filterText.toLowerCase());
  });

  // --- Global Contact Logic (only for filtered clients) ---
  const selectedClientPhones = filteredClients
    .filter((c) => selectedClientIds.includes(c.id))
    .map((c) => c.formDataPurchase?.phone || c.formDataRefinance?.phone || "")
    .filter(Boolean)
    .map((phone) => phone.replace(/\D/g, ""));

  const whatsappMsg = encodeURIComponent(
    "Hola, aquí tienes los teléfonos de tus clientes seleccionados:\n" +
      selectedClientPhones.map((num) => `+1${num}`).join("\n")
  );

  const smsMsg = encodeURIComponent(
    "Hola, aquí tienes los teléfonos de tus clientes seleccionados:\n" +
      selectedClientPhones.map((num) => `+1${num}`).join(", ")
  );

  // --- Select All Logic (only for filtered) ---
  const handleSelectAll = () => {
    const visibleClientIds = filteredClients.map((client) => client.id);
    if (
      visibleClientIds.every((id) => selectedClientIds.includes(id)) &&
      visibleClientIds.length !== 0
    ) {
      setSelectedClientIds(
        selectedClientIds.filter((id) => !visibleClientIds.includes(id))
      );
    } else {
      setSelectedClientIds(
        Array.from(new Set([...selectedClientIds, ...visibleClientIds]))
      );
    }
  };

  const handleCopyPhones = () => {
    navigator.clipboard.writeText(
      selectedClientPhones.map((num) => `+1${num}`).join(", ")
    );
    alert("Selected client phone numbers copied!");
  };

  const handleLogout = () => {
    localStorage.removeItem("officerEmail");
    window.location.href = "/admin-login";
  };
  const handleProfileChange = (field, value) => {
    setEditOfficerInfo((info) => ({
      ...info,
      [field]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, "officers", officerEmail), editOfficerInfo);
      setOfficerInfo(editOfficerInfo);
      setShowEditProfile(false);
      alert("Profile updated successfully!");
    } catch (err) {
      alert("Error updating profile.");
      console.error(err);
    }
  };

  const handleRefresh = () => {
    fetchClients();
  };

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  if (loading) return <div className="officer-dashboard">Loading...</div>;

  return (
    <main className="officer-dashboard">
      <div
        className="officer-dashboard-header"
        style={{ position: "relative" }} // <-- enables absolute positioning inside
      >
        {/* Floating logo top right */}
        {officerInfo && (
          <div className="officer-logo-row">
            <button
              onClick={handleRefresh}
              className="officer-refresh-btn"
              title="Refresh"
              aria-label="Refresh"
            >
              &#x21bb;
            </button>
            <a
              href={`/?officerId=${encodeURIComponent(officerInfo.email)}`}
              className="officer-logo-link"
              title="Create account for client (auto-assigned to you)"
            >
              <img
                src="/assets/HMBHOUSE.png"
                alt="Home Mortgage Logo"
                className="officer-logo-img"
              />
            </a>
          </div>
        )}
        <h1 className="officer-dashboard-title">
          {officerInfo
            ? `${officerInfo.name || "Officer"}`
            : "Officer Dashboard"}
        </h1>
        <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
          <button
            onClick={handleLogout}
            className="officer-logout-btn"
            style={{
              background: "#dc3545",
              color: "#fff",
              padding: "0.3em 0.9em",
              fontWeight: 700,
              fontSize: "1em",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
            }}
          >
            Logout
          </button>
          <button
            onClick={() => setShowEditProfile(true)}
            className="officer-edit-btn"
            style={{
              background: "#0096b2",
              color: "#fff",
              padding: "0.3em 0.9em",
              fontWeight: 700,
              fontSize: "1em",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
            }}
          >
            Edit Profile
          </button>
        </div>

        {officerInfo && (
          <div className="officer-dashboard-credentials">
            <div>
              <strong>{officerInfo.nmlsNumber || "N/A"}</strong>
            </div>
            <div>
              <strong>{officerInfo.mloNumber || "N/A"}</strong>
            </div>
          </div>
        )}
      </div>

      {/* --- Top Bar: View Menu (right) + Search (left) --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "0 0 0.2rem 1rem",
        }}
      >
        <input
          type="text"
          placeholder="Filter clients by name, email, status..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            padding: "0.5em 1em",
            border: "1px solid #cce7e4",
            borderRadius: "6px",
            fontSize: "1em",
            width: "260px",
            maxWidth: "90%",
          }}
        />
        <div
          className="view-dropdown-wrapper"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.7em",
            marginRight: "1.2em",
          }}
        >
          <label htmlFor="status-view-select" className="view-dropdown-label">
            View:
          </label>
          <select
            id="status-view-select"
            className="status-view-select"
            value={statusView}
            onChange={(e) => setStatusView(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* --- Global Contact Buttons and Select All --- */}
      <div
        style={{
          display: "flex",
          gap: 12,
          margin: "0.4rem 0 1.2rem 1rem",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={handleSelectAll}
          className="client-contact-btn"
          style={{
            background: "#eef8ff",
            color: "#232e40",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {filteredClients.length > 0 &&
          filteredClients.every((c) => selectedClientIds.includes(c.id))
            ? "Clear"
            : "All"}
        </button>
        <a
          href={`https://wa.me/?text=${whatsappMsg}`}
          target="_blank"
          rel="noopener noreferrer"
          className="client-contact-btn"
          style={{
            background: "#25d366",
            color: "#fff",
            pointerEvents: selectedClientPhones.length ? "auto" : "none",
            opacity: selectedClientPhones.length ? 1 : 0.5,
          }}
          title="Enviar WhatsApp a seleccionados"
        >
          WhatsApp
        </a>
        <a
          href={`sms:?body=${smsMsg}`}
          className="client-contact-btn"
          style={{
            background: "#00b2c3",
            color: "#fff",
            pointerEvents: selectedClientPhones.length ? "auto" : "none",
            opacity: selectedClientPhones.length ? 1 : 0.5,
          }}
          title="Enviar SMS a seleccionados"
        >
          Text
        </a>
      </div>

      <div className="officer-dashboard-list">
        {filteredClients.length === 0 && (
          <div className="no-clients">No clients found.</div>
        )}
        {filteredClients.map((client) => {
          const email =
            client.formDataPurchase?.email || client.formDataRefinance?.email;
          const phone = (
            client.formDataPurchase?.phone ||
            client.formDataRefinance?.phone ||
            ""
          ).replace(/\D/g, "");
          return (
            <div
              key={client.id}
              className={`officer-client-card${
                client.active === false ? " inactive" : ""
              }`}
            >
              <div className="officer-client-header">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectedClientIds.includes(client.id)}
                    onChange={(e) => {
                      setSelectedClientIds((prev) =>
                        e.target.checked
                          ? [...prev, client.id]
                          : prev.filter((id) => id !== client.id)
                      );
                    }}
                    style={{ marginRight: "0.6em" }}
                  />
                  <span className="officer-client-name">
                    {client.formDataPurchase?.firstName ||
                      client.formDataRefinance?.firstName ||
                      "N/A"}{" "}
                    {client.formDataPurchase?.lastName ||
                      client.formDataRefinance?.lastName ||
                      ""}
                  </span>
                  <span
                    className="officer-client-status"
                    style={{ marginLeft: "1em" }}
                  >
                    {client.active === false ? "Inactive" : "Active"}
                  </span>
                </div>
                <div className="officer-client-date">
                  Assigned: {formatDate(client.createdAt)}
                  <br />
                  Last Contacted: {formatDate(client.lastContactedAt)}
                </div>
              </div>
              <div className="officer-client-contact">
                <a
                  href={`mailto:${email}`}
                  className="client-contact-btn"
                  title="Email"
                  onClick={() => handleContact(client.id)}
                >
                  Email
                </a>
                <a
                  href={`https://wa.me/1${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="client-contact-btn"
                  title="WhatsApp"
                  onClick={() => handleContact(client.id)}
                >
                  WhatsApp
                </a>
                <a
                  href={`tel:${phone}`}
                  className="client-contact-btn"
                  title="Call"
                  onClick={() => handleContact(client.id)}
                >
                  Call
                </a>
              </div>
              <div className="officer-client-actions">
                <button
                  className="view-btn"
                  onClick={() => navigate(`/en/officer-view?id=${client.id}`)}
                >
                  Review
                </button>
                <button
                  className={`inactive-btn${
                    client.active === false ? " active" : ""
                  }`}
                  onClick={() =>
                    setInactive(
                      client.id,
                      client.active === false ? true : false
                    )
                  }
                  title={
                    client.active === false ? "Set Active" : "Set Inactive"
                  }
                >
                  {client.active === false ? "Set Active" : "Set Inactive"}
                </button>
              </div>
              <div className="officer-client-status-summary">
                Status:{" "}
                {client.officerApproved === true
                  ? "Approved"
                  : client.officerApproved === false
                  ? "Denied"
                  : "Pending"}
                {client.officerNote && (
                  <div className="officer-client-note">
                    <strong>Note:</strong> {client.officerNote}
                  </div>
                )}
              </div>
              <div
                style={{
                  marginTop: "0.4em",
                  background: "#f6fbfc",
                  border: "1px solid #cce7e4",
                  borderRadius: "6px",
                  padding: "0.6em 1em",
                  color: "#007b94",
                  fontSize: "0.99em",
                }}
              >
                <strong>Last Note to Client:</strong>
                <div style={{ marginTop: "0.1em", whiteSpace: "pre-line" }}>
                  {client.lastNoteToClient || "--"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* --- Officer Edit Profile Modal --- */}
      {showEditProfile && officerInfo && (
        <div className="officer-edit-modal">
          <div className="officer-edit-content">
            <h2>Edit Profile</h2>
            <form onSubmit={handleProfileSubmit}>
              {[
                "name",
                "nmlsNumber",
                "mloNumber",
                "phone",
                "facebook",
                "instagram",
                "tiktok",
              ].map((field) => (
                <div key={field} style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      fontWeight: 600,
                      minWidth: 90,
                      display: "inline-block",
                    }}
                  >
                    {field.charAt(0).toUpperCase() + field.slice(1)}:
                  </label>
                  <input
                    type="text"
                    value={editOfficerInfo[field] ?? ""}
                    onChange={(e) => handleProfileChange(field, e.target.value)}
                    style={{
                      padding: "0.5em",
                      borderRadius: 6,
                      border: "1px solid #0096b2",
                      width: "75%",
                    }}
                  />
                </div>
              ))}
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="submit"
                  className="officer-save-btn"
                  style={{
                    background: "#0096b2",
                    color: "#fff",
                    fontWeight: 700,
                    padding: "0.6em 1.5em",
                    borderRadius: 6,
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditProfile(false)}
                  style={{
                    background: "#eee",
                    color: "#222",
                    fontWeight: 700,
                    padding: "0.6em 1.5em",
                    borderRadius: 6,
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
            {/* --- Change Password Button --- */}
            <div style={{ marginTop: 28, textAlign: "center" }}>
              <button
                className="officer-change-password-btn"
                style={{
                  background: "#d3eafd",
                  color: "#1664a5",
                  fontWeight: 700,
                  padding: "0.5em 1.3em",
                  borderRadius: 6,
                  border: "1px solid #96c0e6",
                  fontSize: "0.98em",
                  marginTop: 6,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setShowEditProfile(false);
                  setShowChangePassword(true);
                }}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
      {showChangePassword && (
        <div className="officer-edit-modal">
          <div className="officer-edit-content">
            <h2>Change Password</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (newPassword !== confirmPassword) {
                  alert("New passwords do not match.");
                  return;
                }
                setChangingPassword(true);
                try {
                  const auth = getAuth();
                  const user = auth.currentUser;
                  if (!user) {
                    alert("You are not logged in.");
                    setChangingPassword(false);
                    return;
                  }
                  // Re-authenticate with current password
                  const credential = EmailAuthProvider.credential(
                    user.email,
                    currentPassword
                  );
                  await reauthenticateWithCredential(user, credential);
                  // Change password
                  await updatePassword(user, newPassword);
                  alert("Password updated successfully!");
                  setShowChangePassword(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                } catch (err) {
                  alert("Error updating password: " + err.message);
                }
                setChangingPassword(false);
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 600 }}>Current Password:</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={{
                    padding: "0.5em",
                    borderRadius: 6,
                    border: "1px solid #0096b2",
                    width: "80%",
                  }}
                  required
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 600 }}>New Password:</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{
                    padding: "0.5em",
                    borderRadius: 6,
                    border: "1px solid #0096b2",
                    width: "80%",
                  }}
                  required
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontWeight: 600 }}>Confirm New Password:</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    padding: "0.5em",
                    borderRadius: 6,
                    border: "1px solid #0096b2",
                    width: "80%",
                  }}
                  required
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  type="submit"
                  className="officer-save-btn"
                  style={{
                    background: "#0096b2",
                    color: "#fff",
                    fontWeight: 700,
                    padding: "0.6em 1.5em",
                    borderRadius: 6,
                  }}
                  disabled={changingPassword}
                >
                  {changingPassword ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  style={{
                    background: "#eee",
                    color: "#222",
                    fontWeight: 700,
                    padding: "0.6em 1.5em",
                    borderRadius: 6,
                  }}
                  disabled={changingPassword}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
